import React, { useState, useEffect } from 'react';
import PhoneInputCore from 'react-phone-number-input/input';
import { getCountries, getCountryCallingCode, Country } from 'react-phone-number-input/input';
import { parsePhoneNumber } from 'react-phone-number-input';
import en from 'react-phone-number-input/locale/en.json';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './select';

interface PhoneInputFieldProps {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  className?: string;
  onBlur?: () => void;
  onKeyDown?: (e: React.KeyboardEvent) => void;
}

function getFlagIcon(countryCode: string) {
  if (!countryCode) return null;
  return (
    <img
      src={`https://purecatamphetamine.github.io/country-flag-icons/3x2/${countryCode}.svg`}
      alt={countryCode}
      className="w-5 h-[14px] rounded-sm object-cover"
    />
  );
}

export const PhoneInputField = React.forwardRef<HTMLInputElement, PhoneInputFieldProps>(({ value, onChange, placeholder, className = '', onBlur, onKeyDown }, ref) => {
  const [country, setCountry] = useState<Country>('US');
  const [hasUserEdited, setHasUserEdited] = useState(false);

  useEffect(() => {
    if (value && !hasUserEdited) {
      try {
        const parsed = parsePhoneNumber(value);
        if (parsed && parsed.country) {
          setCountry(parsed.country as Country);
        }
      } catch (e) {}
    }
  }, [value, hasUserEdited]);

  const wrapperClasses = `flex w-full items-center gap-2 ${className.includes('h-') ? '' : 'h-11'} ${className}`;
  const inputHeight = className.match(/h-\d+/)?.[0] || 'h-11';

  return (
    <div className={wrapperClasses}>
      <div className="w-fit shrink-0 h-full">
        <Select value={country} onValueChange={(v) => {
          setCountry(v as Country);
          onChange(''); // clear when switching countries to avoid weird formatting
        }}>
          <SelectTrigger className={`${inputHeight} w-fit bg-background border-input px-2.5 [&>svg]:hidden`}>
            <SelectValue>
              <div className="flex items-center gap-2 text-foreground">
                <span className="flex items-center justify-center shrink-0 w-5 h-[14px] overflow-hidden rounded-sm bg-muted/20 border border-border/50 shadow-sm">{getFlagIcon(country)}</span>
                <span className="text-sm font-medium leading-none mt-0.5">+{getCountryCallingCode(country)}</span>
              </div>
            </SelectValue>
          </SelectTrigger>
          <SelectContent className="max-h-[300px]">
            {getCountries().map((c) => (
              <SelectItem key={c} value={c}>
                <div className="flex items-center gap-2.5 text-sm">
                  <span className="flex items-center justify-center shrink-0 w-5 h-[14px] overflow-hidden rounded-sm bg-muted/20 border border-border/50 shadow-sm">{getFlagIcon(c)}</span>
                  <span className="truncate max-w-[120px] font-medium" title={en[c as keyof typeof en]}>{en[c as keyof typeof en]}</span>
                  <span className="text-muted-foreground ml-auto pl-2">+{getCountryCallingCode(c)}</span>
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <PhoneInputCore
        country={country}
        value={value}
        onChange={(v) => {
          setHasUserEdited(true);
          onChange(v || '');
        }}
        placeholder={placeholder}
        onBlur={onBlur}
        onKeyDown={onKeyDown as any}
        className={`flex-1 w-full ${inputHeight} rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring`}
        ref={ref as any}
      />
    </div>
  );
});

PhoneInputField.displayName = 'PhoneInputField';
